import React from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography, Box, Chip, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  value: {
    color: theme.palette.text.primary,
  },
  description: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
}));

const TASK_TYPES = {
  1: 'Call',
  2: 'Vendor Call',
  3: 'Email',
  4: 'Vendor Email',
  5: 'Letter',
  6: 'Voucher',
  7: 'Vendor Letter',
  8: 'Vendor Voucher',
};

const TASK_IMPORTANCE = {
  1: 'Low',
  2: 'Normal',
  3: 'High',
};

const SERVICES = {
  0: 'Homesale',
  1: 'Global',
  2: 'Destination',
  3: 'Candidate/Intern',
  4: 'Temporary Living',
  5: 'HHG',
};

const TaskPreviewCard = ({ ruleData, validTriggerFields, isLetterRule }) => {
  const classes = useStyles();

  const getDueDateText = () => {
    const { dueDateDaysOffset, dueDateBasedOnTrigger, dueDateTriggerFieldId, dueDateTriggerFieldName } = ruleData;

    if (isLetterRule) {
      return dueDateDaysOffset > 0
        ? `${dueDateDaysOffset} day${dueDateDaysOffset !== 1 ? 's' : ''} after the letter is sent`
        : 'The day the letter is sent';
    }

    if (!dueDateBasedOnTrigger) {
      const direction = dueDateDaysOffset === 0 ? 'on' : dueDateDaysOffset > 0 ? 'after' : 'before';
      const days = Math.abs(dueDateDaysOffset);
      return `${days} day${days !== 1 ? 's' : ''} ${direction} task creation`;
    }

    if (!dueDateTriggerFieldId && !dueDateTriggerFieldName) {
      return 'Based on trigger field (not selected)';
    }

    // Resolve by catalog id first; fall back to the legacy name for rules saved before ids existed
    const field = (dueDateTriggerFieldId && validTriggerFields.find(f => f.validTriggerFieldId === dueDateTriggerFieldId))
      || (dueDateTriggerFieldName && validTriggerFields.find(f => f.fieldName === dueDateTriggerFieldName))
      || null;
    const fieldDisplay = field ? field.displayName : (dueDateTriggerFieldName || 'selected field');
    const direction = dueDateDaysOffset === 0 ? 'on' : dueDateDaysOffset > 0 ? 'after' : 'before';
    const days = Math.abs(dueDateDaysOffset);

    if (dueDateDaysOffset === 0) {
      return `On the same day as ${fieldDisplay}`;
    }

    return `${days} day${days !== 1 ? 's' : ''} ${direction} ${fieldDisplay}`;
  };

  const getImportanceColor = () => {
    switch (ruleData.taskImportanceId) {
      case 1:
        return 'default';
      case 3:
        return 'secondary';
      default:
        return 'primary';
    }
  };

  if (!ruleData.ruleName || !ruleData.taskTypeId) {
    return (
      <Paper className={classes.container}>
        <Typography variant="h6" gutterBottom>
          Task Preview
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Fill in rule details to see a preview of the generated tasks.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper className={classes.container}>
      <Typography variant="h6" gutterBottom>
        Task Preview
      </Typography>
      <Typography variant="caption" color="textSecondary" gutterBottom>
        This is what tasks created by this rule will look like:
      </Typography>

      <Divider className={classes.divider} />

      <Box className={classes.row}>
        <span className={classes.label}>Title:</span>
        <span className={classes.value}>{ruleData.ruleName}</span>
      </Box>

      <Box className={classes.row}>
        <span className={classes.label}>Type:</span>
        <span className={classes.value}>{TASK_TYPES[ruleData.taskTypeId] || 'Unknown'}</span>
      </Box>

      <Box className={classes.row}>
        <span className={classes.label}>Priority:</span>
        <Chip
          label={TASK_IMPORTANCE[ruleData.taskImportanceId] || 'Normal'}
          color={getImportanceColor()}
          size="small"
        />
      </Box>

      <Box className={classes.row}>
        <span className={classes.label}>Due:</span>
        <span className={classes.value}>{getDueDateText()}</span>
      </Box>

      <Box className={classes.row}>
        <span className={classes.label}>Service:</span>
        <span className={classes.value}>{SERVICES[ruleData.serviceId] || 'Unknown'}</span>
      </Box>

      {ruleData.description && (
        <Box className={classes.description}>
          <Typography variant="caption" className={classes.label}>
            Description:
          </Typography>
          <Typography variant="body2" style={{ marginTop: 4 }}>
            {ruleData.description}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

TaskPreviewCard.propTypes = {
  ruleData: PropTypes.shape({
    ruleName: PropTypes.string,
    description: PropTypes.string,
    taskTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    taskImportanceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    serviceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dueDateDaysOffset: PropTypes.number,
    dueDateBasedOnTrigger: PropTypes.bool,
    dueDateTriggerFieldId: PropTypes.number,
    dueDateTriggerFieldName: PropTypes.string,
  }).isRequired,
  validTriggerFields: PropTypes.array,
  isLetterRule: PropTypes.bool,
};

TaskPreviewCard.defaultProps = {
  validTriggerFields: [],
  isLetterRule: false,
};

export default TaskPreviewCard;
